import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/Button';
import { TermsModal } from '@/components/common/TermsModal';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import {
  APP_FOOTER_LINK_COLUMNS,
  APP_FOOTER_STRINGS as S,
  SUPPORT_EMAIL,
} from '@/lib/constants/app-footer-strings';

const CURRENT_YEAR = new Date().getFullYear();

const HEADING_CLASSES = 'm-0 text-sm font-semibold tracking-[0.05em] text-white uppercase';
const LINK_CLASSES =
  'cursor-pointer rounded text-slate transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-green focus-visible:outline-none';

/**
 * Footer for logged-in pages (MYN-253): support contact and links to the
 * internal pages every persona can open. Lighter than the public marketing
 * footer, which stays on public pages only.
 */
export function AppFooter() {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <>
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
      <footer className="bg-navy px-6 py-10 text-white sm:px-10">
        <div className="mx-auto flex max-w-[1312px] flex-col gap-8">
          <div className="flex flex-col justify-between gap-8 md:flex-row">
            <div className="flex max-w-xs flex-col gap-3">
              <Link
                to={ROUTES.DASHBOARD}
                aria-label={S.LOGO_LINK_LABEL(UI_STRINGS.BRAND_NAME)}
                className="w-fit"
              >
                <Logo variant="white" height={48} aria-hidden />
              </Link>
              <p className="text-slate m-0 text-sm">{S.TAGLINE}</p>
            </div>

            <nav aria-label={S.QUICK_LINKS_HEADING} className="flex flex-col gap-3">
              <h2 className={HEADING_CLASSES}>{S.QUICK_LINKS_HEADING}</h2>
              <div className="flex gap-12">
                {APP_FOOTER_LINK_COLUMNS.map((column) => (
                  <ul key={column[0].to} className="m-0 flex list-none flex-col gap-2 p-0">
                    {column.map(({ label, to }) => (
                      <li key={to}>
                        <Link to={to} className={LINK_CLASSES}>
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </nav>

            <div className="flex max-w-xs flex-col gap-3">
              <h2 className={HEADING_CLASSES}>{S.SUPPORT_HEADING}</h2>
              <p className="text-slate m-0 text-sm">{S.SUPPORT_TEXT}</p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={`${LINK_CLASSES} w-fit font-semibold text-white`}
              >
                {S.EMAIL_SUPPORT}: {SUPPORT_EMAIL}
              </a>
            </div>
          </div>

          <hr className="m-0 h-px border-none bg-white/10" />

          <div className="flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
            <p className="m-0 text-white/50">
              © {CURRENT_YEAR} {UI_STRINGS.BRAND_NAME}. {S.RIGHTS_RESERVED}
            </p>
            <Button
              variant="link"
              className="font-normal! text-white/50! hover:text-white!"
              onClick={() => setTermsOpen(true)}
            >
              {S.TERMS}
            </Button>
          </div>
        </div>
      </footer>
    </>
  );
}
